'use client';
import React, { useCallback, useState, useEffect, Suspense } from 'react';
import { Button, ErrorsBox, Icon } from '@benzinga/core-ui';
import { AuthBoxWrapper, AuthInput, AuthPasswordInput } from '../AuthComponents';
import { Authentication, AuthenticationManager, RegisterUser } from '@benzinga/session';
import { SessionContext } from '@benzinga/session-context';
import { SafeType } from '@benzinga/safe-await';
import { useTranslation } from 'react-i18next';
import { TrackingManager } from '@benzinga/tracking-manager';
import styled from '@benzinga/themetron';
import { ParsedUrlQuery } from 'querystring';
import { faEnvelope, faPhone, faUser } from '@fortawesome/pro-regular-svg-icons';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
import i18n, { LocaleType } from '@benzinga/translate';
import { GlobalRegisterType, getRegisterTypeByIdentity } from '@benzinga/identity';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { GOOGLE_SITE_KEY } from '../../config';
import { ReCaptchaComponent } from '../AuthModal/ReCaptcha';
import SocialButton from '../AuthIterations/SocialButton';
import { AuthMode } from '@benzinga/session';
import { useCustomRouter } from '@benzinga/hooks';
import { UserManager } from '@benzinga/user-manager';

export interface RegisterProps {
  initialEmail?: string;
  showUpsell?: boolean;
  onRegister?: (auth: SafeType<Authentication>, authFor: string) => void;
  onSocialClicked?: (socialAuth: string, forceUrl?: string) => void;
  onError?: (errors: string | string[] | null) => void;
  setAuthMode?: (authMode: AuthMode) => void;
  setAuthentication?: (auth: SafeType<Authentication> | null) => void;
  setPhoneNumber?: (phoneNumber: string) => void;
  setOfferEmail?: (email: string) => void;
  isPaywalled?: boolean;
}
interface State {
  registerAuth: SafeType<Authentication> | null;
  isRegistering: boolean;
  error: string;
  errors: string[] | null;
}

interface RegisterUserInput {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  phoneNumber: string;
}
export const Register = ({
  initialEmail,
  isPaywalled = false,
  onError,
  onRegister,
  onSocialClicked,
  setAuthentication,
  setAuthMode,
  setOfferEmail,
  setPhoneNumber,
  showUpsell = false,
}: RegisterProps) => {
  const router = useCustomRouter();
  const [registerInput, setRegisterInput] = useState<RegisterUserInput>({
    email: initialEmail ?? '',
    firstname: '',
    lastname: '',
    password: '',
    phoneNumber: '',
  });
  const [state, setState] = React.useState<State>({
    error: '',
    errors: null,
    isRegistering: false,
    registerAuth: null,
  });
  const [passwordHidden, setPasswordHidden] = useState(true);

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [reCaptchaNonce, setReCaptchaNonce] = useState<number>(0);

  const isFirstNameRequired = router.query?.firstname === 'required';
  const isLastNameRequired = router.query?.lastname === 'required';
  const isPhoneRequired = router.query?.phone === 'required';

  const { t } = useTranslation(['auth', 'common']);
  const session = React.useContext(SessionContext);

  const handleReCaptchaVerify = useCallback((token: string | null) => {
    if (token) {
      setCaptchaToken(token);
    }
  }, []);

  const updateNonce = useCallback(() => {
    setReCaptchaNonce(r => r + 1);
  }, [setReCaptchaNonce]);

  useEffect(() => {
    updateNonce();
    if (onError && (state.error || state.errors)) {
      onError(state.errors || state.error);
    }
  }, [state.error, state.errors, onError, updateNonce]);

  const handleRegisterPostProcess = async (data: SafeType<Authentication> | null) => {
    await session
      .getManager(TrackingManager)
      .trackAuthEvent('register', { auth_type: 'email', is_paywalled: isPaywalled });
    session.getManager(UserManager).setGlobalSetting('default_site', window.location.host);
    if (data) onRegister && onRegister(data, 'register');
  };

  const getRegistrationError = (query: ParsedUrlQuery, input: RegisterUserInput): string => {
    if (input.password.trim() === '') {
      return t('Inputs.Error.is-required', { field: t('Inputs.password', { ns: 'common' }), ns: 'common' });
    } else if (input.email.trim() === '') {
      return t('Inputs.Error.is-required', { field: 'Email', ns: 'common' });
    } else if (query.firstname === 'required' && input.firstname.trim() === '') {
      return 'First Name is required.';
    } else if (query.lastname === 'required' && input.lastname.trim() === '') {
      return 'Last Name is required.';
    } else if (query.phone === 'required' && input.phoneNumber.trim() === '') {
      return 'Phone number is required.';
    } else {
      return '';
    }
  };

  const getNewUser = (
    email: string,
    firstname?: string,
    lastname?: string,
    password?: string,
    phoneNumber?: string,
    captcha?: string,
    globalRegisterType?: GlobalRegisterType,
  ): RegisterUser => {
    const newUser: RegisterUser = {
      email: email,
      password: password ?? '',
    };
    if (firstname) {
      newUser.firstName = firstname;
    }
    if (lastname) {
      newUser.lastName = lastname;
    }
    if (phoneNumber) {
      newUser.phoneNumber = phoneNumber;
    }
    if (captcha) {
      newUser.captcha = captcha;
    }
    if (globalRegisterType) {
      newUser.profileData = {
        globalAccountType: globalRegisterType,
      };
    }

    return newUser;
  };

  const register = async () => {
    setState(prev => ({ ...prev, isRegistering: true }));
    const { email, firstname, lastname, password, phoneNumber } = registerInput;
    const newUser = getNewUser(
      email,
      firstname,
      lastname,
      password,
      phoneNumber,
      captchaToken ?? undefined,
      getRegisterTypeByIdentity(window.location.host, i18n.language as LocaleType) ?? undefined,
    );
    setPhoneNumber && setPhoneNumber(phoneNumber);
    const error = getRegistrationError(router.query, registerInput);
    if (error) {
      setState(prev => ({ ...prev, error, isRegistering: false }));
      return;
    }

    const registerResponse: SafeType<Authentication> = await session
      .getManager(AuthenticationManager)
      .register(newUser, router.query?.utm_source as string);
    if (registerResponse.err) {
      if (registerResponse.err.message) {
        setState(prev => ({ ...prev, error: registerResponse.err.message, isRegistering: false }));
      } else if (registerResponse.err.data instanceof Response) {
        const errors = await registerResponse.err.data.json();
        delete errors.confirm_password;
        setState(prev => ({ ...prev, errors, isRegistering: false }));
      } else {
        setState(prev => ({ ...prev, error: 'Error occured', isRegistering: false }));
      }
      window.parent.postMessage(`register_failed`, '*');
    } else {
      setState(prev => ({ ...prev, isRegistering: false }));
      setAuthentication && setAuthentication(registerResponse);

      if (router.query?.phone === 'required') {
        setAuthMode && setAuthMode('sms-verify');
      } else if (showUpsell) {
        setOfferEmail && setOfferEmail(email);
        setAuthMode && setAuthMode('report-offer');
      } else {
        setAuthMode && setAuthMode('complete');
        handleRegisterPostProcess(registerResponse);
      }
    }
  };

  const togglePasswordHidden = useCallback(() => {
    setPasswordHidden(!passwordHidden);
  }, [passwordHidden, setPasswordHidden]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRegisterInput(prevState => ({ ...prevState, [name]: value }));
  };

  useEffect(() => {
    setPhoneNumber && setPhoneNumber(registerInput.phoneNumber);
  }, [registerInput.phoneNumber, setPhoneNumber]);

  return (
    <>
      <div className="register-social-buttons flex gap-2 mt-8 mb-4">
        <SocialButton onSocialClicked={onSocialClicked} socialType="google" />
        <SocialButton onSocialClicked={onSocialClicked} socialType="apple" />
        <SocialButton onSocialClicked={onSocialClicked} socialType="microsoft" />
      </div>
      <div className="flex-1 text-center mb-8 relative register-continue-row">
        <div className="w-full h-[1px] bg-blue-400 absolute top-1/2 left-0"></div>
        <span className="text-blue-400 relative bg-blue-950 px-4">{t('Auth.AuthModal.continue-row')}</span>
      </div>
      <AuthBoxWrapper className="register-auth-box-wrapper">
        <RegisterInputContainer>
          <div className="input-container mb-2">
            {isFirstNameRequired && (
              <AuthInput
                autoComplete="given-name"
                label={<Icon className="label-icon" icon={faUser} />}
                name="firstname"
                onChange={handleInputChange}
                placeholder="John"
                type="text"
                value={registerInput.firstname}
              />
            )}
            {isLastNameRequired && (
              <AuthInput
                autoComplete="family-name"
                label={<Icon className="label-icon" icon={faUser} />}
                name="lastname"
                onChange={handleInputChange}
                placeholder="Doe"
                type="text"
                value={registerInput.lastname}
              />
            )}
            {isPhoneRequired && (
              <AuthInput
                autoComplete="tel"
                label={<Icon className="label-icon" icon={faPhone} />}
                name="phoneNumber"
                onChange={handleInputChange}
                placeholder="1 (877) 440-9676"
                type="tel"
                value={registerInput.phoneNumber}
              />
            )}
            <AuthInput
              autoComplete="email"
              id="email"
              label={<Icon className="label-icon" icon={faEnvelope} />}
              name="email"
              onChange={handleInputChange}
              placeholder="john.doe@example.com"
              type="email"
              value={registerInput.email}
            />
            <AuthPasswordInput
              autoComplete="new-password"
              hidden={passwordHidden}
              id="new-password"
              label={<Icon className="label-icon" icon={faLock} />}
              name="password"
              onChange={handleInputChange}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  register();
                }
              }}
              placeholder="***********"
              toggleHidden={togglePasswordHidden}
              value={registerInput.password}
            />
          </div>
          <div className="action-container register-action-container select-none">
            <BtnMain isLoading={state.isRegistering} onClick={() => register()}>
              {t('Auth.Register.button')}
            </BtnMain>
          </div>
          {(state.errors || state.error) && <ErrorsBox error={state.error} errors={state.errors} includeKeys={true} />}
        </RegisterInputContainer>

        <div className="mt-4 text-center flex items-center justify-between p-4 font-lg border border-blue-100 register-toggle-box">
          <div>{t('Auth.AuthModal.register.toggle-box.text1')} </div>
          <div
            className="action-text select-none flex gap-2 items-center cursor-pointer"
            onClick={() => {
              setAuthMode && setAuthMode('login');
            }}
          >
            {t('Auth.AuthModal.register.toggle-box.text2')}
            <Icon icon={faArrowRight} />
          </div>
        </div>

        <div className="h-0 invisible">
          <Suspense fallback={<div />}>
            <GoogleReCaptchaProvider
              container={{ element: 'google-recaptcha-reg', parameters: { badge: 'inline', theme: 'light' } }}
              reCaptchaKey={GOOGLE_SITE_KEY}
            >
              <ReCaptchaComponent nonce={reCaptchaNonce} onToken={handleReCaptchaVerify} />
            </GoogleReCaptchaProvider>
          </Suspense>
        </div>
        <div className="flex justify-center w-full min-h-[60px]" id="google-recaptcha-reg"></div>
      </AuthBoxWrapper>
    </>
  );
};

const RegisterInputContainer = styled.form`
  .input-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .label-icon {
    font-size: 24px;
    color: #99aecc;
    margin-bottom: 8px;
    svg {
      height: 24px;
      width: 24px;
    }
  }
`;
const BtnMain = styled(Button)`
  color: white;
  background-color: #3f83f8 !important;
  padding: 16px 32px;

  &:hover {
    background-color: #3268c4;
    color: white;
  }
`;
